import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Cog6ToothIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { fetchUser } from "../../../api/users";
import { fetchOfferSettings } from "../../../api/offer-settings";
import RoleOnly from "../../../routes/roleOnly";
import { offerSetupIssues } from "../companyGuide/OfferSetupStatus";

const SIGNATURE_DISMISS_KEY = "promang.offers.prompt.signature.dismissed";
const ADMIN_DISMISS_KEY = "promang.offers.prompt.admin_settings.dismissed";

function readDismissed(key) {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(key) {
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    /* ignore quota / private mode */
  }
}

function FloatingPromptCard({
  icon: Icon,
  title,
  body,
  to,
  linkLabel,
  onDismiss,
  dismissLabel,
}) {
  return (
    <div
      role="status"
      className="pointer-events-auto flex w-[min(28rem,calc(100vw-1.5rem))] flex-col gap-3 rounded-xl border border-amber-200 bg-white/95 px-4 py-3 shadow-[0_16px_44px_rgba(15,23,42,0.16),0_4px_12px_rgba(15,23,42,0.08)] backdrop-blur sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-800">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <p className="mt-0.5 text-sm text-slate-600">{body}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {to ? (
          <Link
            to={to}
            className="inline-flex items-center rounded-lg bg-[#1463C7] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0B3A78]"
          >
            {linkLabel}
          </Link>
        ) : null}
        {onDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            {dismissLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * First-visit / missing-setup prompts for the offers module.
 * Floating toast (bottom) so it never covers breadcrumbs / OfferSummaryHeader.
 * - Personal: upload signature (dismissible via localStorage).
 * - Admin: recommended company offer feature flags (separate from personal).
 */
export default function OfferSetupPrompts({ forceSignature = false }) {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const currentCompany = useSelector((state) => state.session.currentCompany);
  const isAdmin = Boolean(currentUser?.roles?.some((r) => r.name === "admin"));

  const offersModuleOn =
    !Array.isArray(currentCompany?.active_modules) ||
    currentCompany.active_modules.length === 0 ||
    currentCompany.active_modules.includes("offers");

  const [signatureUrl, setSignatureUrl] = useState(
    currentUser?.signature_url || null
  );
  const [signatureLoaded, setSignatureLoaded] = useState(
    Boolean(currentUser?.signature_url)
  );
  const [signatureDismissed, setSignatureDismissed] = useState(() =>
    readDismissed(SIGNATURE_DISMISS_KEY)
  );
  const [adminDismissed, setAdminDismissed] = useState(() =>
    readDismissed(ADMIN_DISMISS_KEY)
  );
  const [offersState, setOffersState] = useState({
    flags: null,
    recommended: [],
    loadError: false,
  });

  useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    fetchUser(currentUser.id, { fields: "id,signature_url" })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data !== undefined ? res.data : res;
        setSignatureUrl(data?.signature_url || null);
        setSignatureLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setSignatureLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  useEffect(() => {
    if (!offersModuleOn || !isAdmin) return;
    let cancelled = false;
    fetchOfferSettings()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data !== undefined ? res.data.data : res?.data;
        setOffersState({
          flags: data?.flags && typeof data.flags === "object" ? data.flags : {},
          recommended: Array.isArray(data?.recommended) ? data.recommended : [],
          loadError: false,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setOffersState({ flags: null, recommended: [], loadError: true });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [offersModuleOn, isAdmin, currentCompany?.id]);

  const missingSignature = signatureLoaded && !String(signatureUrl || "").trim();
  // forceSignature only strengthens copy on Dokument/Kontroll; dismiss always wins.
  const showSignature = missingSignature && !signatureDismissed;

  const adminIssues = useMemo(
    () =>
      offersModuleOn
        ? offerSetupIssues({
            flags: offersState.flags,
            recommended: offersState.recommended,
            loadError: offersState.loadError,
          })
        : [],
    [offersModuleOn, offersState]
  );
  const showAdmin =
    isAdmin &&
    !adminDismissed &&
    adminIssues.length > 0 &&
    !offersState.loadError;

  if (!offersModuleOn || (!showSignature && !showAdmin)) {
    return null;
  }

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 bottom-[max(1.5rem,calc(var(--mobile-nav-height,0px)+1rem+env(safe-area-inset-bottom,0px)))] z-[110] flex flex-col items-center gap-2 px-3"
      aria-live="polite"
    >
      {showSignature ? (
        <FloatingPromptCard
          icon={PencilSquareIcon}
          title={t("offers.setup_prompt.signature.title")}
          body={
            forceSignature
              ? t("offers.setup_prompt.signature.body_force")
              : t("offers.setup_prompt.signature.body")
          }
          to="/dashboard/user/settings#signature"
          linkLabel={t("offers.setup_prompt.signature.cta")}
          onDismiss={() => {
            writeDismissed(SIGNATURE_DISMISS_KEY);
            setSignatureDismissed(true);
          }}
          dismissLabel={t("offers.setup_prompt.dismiss")}
        />
      ) : null}

      {showAdmin ? (
        <RoleOnly role="admin">
          <FloatingPromptCard
            icon={Cog6ToothIcon}
            title={t("offers.setup_prompt.admin.title")}
            body={t("offers.setup_prompt.admin.body", {
              count: adminIssues.length,
            })}
            to="/dashboard/offers/settings"
            linkLabel={t("offers.setup_prompt.admin.cta")}
            onDismiss={() => {
              writeDismissed(ADMIN_DISMISS_KEY);
              setAdminDismissed(true);
            }}
            dismissLabel={t("offers.setup_prompt.dismiss")}
          />
        </RoleOnly>
      ) : null}
    </div>,
    document.body
  );
}
